/**
 * CAPA draft mapper. Enum casing matches CAPACreate.js exactly:
 * capa_type/priority lowercase, severity Capitalized.
 *
 * `source`/`source_id` are never in the draft (owned by the existing
 * URLSearchParams prefill from incidents/audits); the mapper drops them if a
 * drifted backend ever emits them.
 */

import { applyDraftWithSpecs, coerceDate, coerceEnum } from './mergeUtils';

export const CAPA_TYPES = ['corrective', 'preventive', 'immediate'];
export const CAPA_SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
export const CAPA_PRIORITIES = ['low', 'medium', 'high', 'critical'];

function futureDate(value) {
  const d = coerceDate(value);
  if (!d) return null;
  return d > new Date().toISOString().slice(0, 10) ? d : null;
}

function resolveDepartmentId(name, ctx) {
  const departments = ctx.departments || [];
  const hit = departments.find(
    (d) => (d.name || '').toLowerCase() === String(name).trim().toLowerCase()
  );
  return hit ? hit.id : null;
}

/** Find a tenant user by name, for resolving an AI-named assignee to an id. */
function findAssignableUser(name, ctx) {
  const users = ctx.assignableUsers || [];
  return users.find(
    (u) => (u.name || '').toLowerCase() === String(name).trim().toLowerCase()
  ) || null;
}

/**
 * The backend emits a NAME (capa_adapter.assigned_to_name) but the form's
 * Assigned To is a select bound to the user ID, so the name has to be resolved
 * against the tenant's own directory. Fails closed: an unmatched name yields
 * null, which the merge driver reports as "not valid — left blank" rather than
 * inventing an assignee. Mirrors resolveDepartmentId above.
 */
function resolveAssigneeId(name, ctx) {
  const hit = findAssignableUser(name, ctx);
  return hit ? hit.id : null;
}

const FIELD_SPECS = {
  title: { label: 'Title', coerce: (v) => String(v).slice(0, 500) },
  description: { label: 'Description', coerce: (v) => String(v).slice(0, 5000) },
  capa_type: { label: 'CAPA Type', coerce: (v) => coerceEnum(v, CAPA_TYPES) },
  severity: { label: 'Severity', coerce: (v) => coerceEnum(v, CAPA_SEVERITIES) },
  priority: { label: 'Priority', coerce: (v) => coerceEnum(v, CAPA_PRIORITIES) },
  root_cause: { label: 'Root Cause', coerce: (v) => String(v).slice(0, 2000) },
  due_date: { label: 'Due Date', coerce: futureDate },
  department: {
    label: 'Department',
    formKey: 'department_id',
    coerce: resolveDepartmentId,
  },
  assigned_to_name: {
    label: 'Assigned To',
    formKey: 'assigned_to',
    coerce: resolveAssigneeId,
  },
};

/**
 * applyDraft(draft, currentFormData, ctx) →
 *   { formData, applied, skipped, warnings }
 *
 * ctx = { departments: [{id, name}], assignableUsers: [{id, name, role}] }
 *
 * Pure; fill-empty-only. Also keeps assigned_to_name in step with the
 * assigned_to id it resolved, the same invariant the form's own select
 * maintains — a stored display name must never disagree with the assignee.
 */
export function applyDraft(draft, currentFormData, ctx = {}) {
  const result = applyDraftWithSpecs(draft || {}, currentFormData, FIELD_SPECS, ctx);

  if (result.applied.some((a) => a.path === 'assigned_to')) {
    const hit = findAssignableUser(draft.assigned_to_name, ctx);
    if (hit) result.formData.assigned_to_name = hit.name;
  }

  return result;
}

/** "Start from this" mapping for a similar CAPA record (no dates/people). */
export function draftFromSimilarRecord(record) {
  if (!record) return {};
  return {
    title: record.title,
    description: record.description,
    capa_type: record.capa_type,
    priority: record.priority,
    root_cause: record.root_cause,
  };
}
